<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PixKeysAPI from 'dashboard/api/talkviaPixKeys';
import SectionLayout from './SectionLayout.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  isValidPixKey,
  normalizePixKey,
  displayPixKey,
} from 'dashboard/helper/pixKeyValidation';

const props = defineProps({
  companyProfile: { type: Object, default: () => ({}) },
});
const { t } = useI18n();
const keys = ref([]);
const loading = ref(true);
const saving = ref(false);
const error = ref('');
const loadFailed = ref(false);
const editingId = ref(null);
const form = reactive({});
const keyTouched = ref(false);
const deletion = ref(null);
const deleteDialog = ref(null);
const mfaEnabled = ref(false);
const mfaMethod = ref('authenticator');
const otpCode = ref('');
const requestingCode = ref(false);
const codeSent = ref(false);
const codeMessage = ref('');
const needsDeliveryCode = computed(() =>
  ['email', 'whatsapp'].includes(mfaMethod.value)
);
const codeValid = computed(() => /^\d{6}$/.test(otpCode.value.trim()));
const typeLabels = computed(() => ({
  CNPJ: t('PIX_KEYS.TYPES.CNPJ'),
  CPF: t('PIX_KEYS.TYPES.CPF'),
  EMAIL: t('PIX_KEYS.TYPES.EMAIL'),
  PHONE: t('PIX_KEYS.TYPES.PHONE'),
  EVP: t('PIX_KEYS.TYPES.EVP'),
}));
const types = computed(() =>
  Object.entries(typeLabels.value).map(([value, label]) => ({ value, label }))
);
const keyValid = computed(() => isValidPixKey(form.key_type, form.key));
const keyMessage = computed(() => {
  if (keyValid.value) return t('PIX_KEYS.VALIDATION.VALID');
  if (!keyTouched.value) return '';
  return t(`PIX_KEYS.VALIDATION.${form.key_type}`);
});
const keyInputType = computed(
  () => ({ EMAIL: 'email', PHONE: 'tel' })[form.key_type] || 'text'
);
const canSave = computed(
  () =>
    keyValid.value &&
    ['label', 'merchant_name', 'merchant_city'].every(field =>
      form[field]?.trim()
    )
);

const changeKeyType = type => {
  if (type === form.key_type) return;
  form.key_type = type;
  form.key = '';
  keyTouched.value = false;
};

const validateKey = () => {
  keyTouched.value = true;
  form.key = displayPixKey(
    form.key_type,
    normalizePixKey(form.key_type, form.key)
  );
};

const load = async () => {
  loading.value = true;
  loadFailed.value = false;
  error.value = '';
  try {
    const { data } = await PixKeysAPI.get();
    keys.value = data.payload;
    mfaEnabled.value = data.security?.mfa_enabled === true;
    mfaMethod.value = data.security?.method || 'authenticator';
  } catch {
    error.value = t('PIX_KEYS.LOAD_ERROR');
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
};

const edit = key => {
  error.value = '';
  keyTouched.value = false;
  editingId.value = key?.id || '';
  otpCode.value = '';
  codeSent.value = false;
  codeMessage.value = '';
  Object.assign(form, {
    label: '',
    key_type: 'CNPJ',
    key: props.companyProfile.cnpj || '',
    merchant_name:
      props.companyProfile.nome_fantasia ||
      props.companyProfile.razao_social ||
      '',
    merchant_city: props.companyProfile.cidade || '',
    ...key,
  });
  form.key = displayPixKey(form.key_type, form.key);
};

const requestCode = async () => {
  if (requestingCode.value) return;
  requestingCode.value = true;
  codeMessage.value = '';
  try {
    await PixKeysAPI.requestCode();
    codeSent.value = true;
    codeMessage.value = t('PIX_KEYS.SECURITY.SENT');
  } catch (failure) {
    codeSent.value = false;
    codeMessage.value =
      failure?.response?.data?.error || t('PIX_KEYS.SECURITY.SEND_ERROR');
  } finally {
    requestingCode.value = false;
  }
};

const cancel = () => {
  editingId.value = null;
  otpCode.value = '';
};

const save = async () => {
  if (!canSave.value || !mfaEnabled.value || !codeValid.value || saving.value)
    return;
  saving.value = true;
  error.value = '';
  try {
    const attributes = {
      ...form,
      key:
        form.key_type === 'PHONE'
          ? form.key.trim()
          : normalizePixKey(form.key_type, form.key),
      otp_code: otpCode.value.trim(),
    };
    delete attributes.id;
    const { data } = editingId.value
      ? await PixKeysAPI.update(editingId.value, attributes)
      : await PixKeysAPI.create(attributes);
    keys.value = editingId.value
      ? keys.value.map(key => (key.id === editingId.value ? data : key))
      : [...keys.value, data];
    cancel();
    useAlert(t('PIX_KEYS.SAVED'));
  } catch (failure) {
    error.value = failure?.response?.data?.error || t('PIX_KEYS.SAVE_ERROR');
    otpCode.value = '';
    if (failure?.response?.data?.code === 'pix_mfa_required')
      mfaEnabled.value = false;
  } finally {
    saving.value = false;
  }
};

const confirmDelete = key => {
  deletion.value = key;
  deleteDialog.value.open();
};

const remove = async () => {
  if (!deletion.value || saving.value) return;
  saving.value = true;
  error.value = '';
  try {
    await PixKeysAPI.delete(deletion.value.id);
    keys.value = keys.value.filter(key => key.id !== deletion.value.id);
    deleteDialog.value.close();
    useAlert(t('PIX_KEYS.DELETED'));
  } catch (failure) {
    error.value = failure?.response?.data?.error || t('PIX_KEYS.DELETE_ERROR');
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <SectionLayout
    :title="t('PIX_KEYS.TITLE')"
    :description="t('PIX_KEYS.HELP')"
    icon="i-hugeicons-qr-code"
    with-border
  >
    <div class="grid min-w-0 grid-cols-1 gap-4">
      <p v-if="loading" class="mb-0 text-sm text-n-slate-11" role="status">
        {{ t('PIX_KEYS.LOADING') }}
      </p>
      <p v-if="error" class="mb-0 text-sm text-n-ruby-11" role="alert">
        {{ error }}
      </p>
      <NextButton
        v-if="loadFailed"
        :label="t('PIX_KEYS.RETRY')"
        variant="faded"
        class="justify-self-start"
        @click="load"
      />
      <template v-if="!loading && !loadFailed">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span class="inline-flex items-center gap-2 text-sm text-n-slate-11">
            <span class="i-hugeicons-shield-01 size-4" aria-hidden="true" />
            {{
              t(
                mfaEnabled
                  ? 'PIX_KEYS.SECURITY.ACTIVE'
                  : 'PIX_KEYS.SECURITY.REQUIRED'
              )
            }}
          </span>
          <NextButton
            v-if="editingId === null"
            :label="t('PIX_KEYS.ADD')"
            icon="i-lucide-plus"
            size="sm"
            :disabled="saving || !mfaEnabled || keys.length >= 20"
            @click="edit()"
          />
        </div>
        <div
          v-if="!mfaEnabled"
          class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <div class="min-w-0 flex-1 text-sm text-n-slate-11">
            {{ t('PIX_KEYS.SECURITY.SETUP_NOTICE') }}
          </div>
          <RouterLink
            :to="{ name: 'profile_settings_mfa' }"
            class="shrink-0 text-sm font-medium text-n-brand hover:underline"
          >
            {{ t('PIX_KEYS.SECURITY.SETUP') }}
          </RouterLink>
        </div>
        <div
          v-if="!keys.length"
          class="grid justify-items-center gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-6 text-center"
        >
          <span
            class="i-hugeicons-qr-code size-8 text-n-slate-10"
            aria-hidden="true"
          />
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('PIX_KEYS.EMPTY') }}
          </p>
          <p class="mb-0 max-w-lg text-xs text-n-slate-11">
            {{ t('PIX_KEYS.EMPTY_HELP') }}
          </p>
        </div>
        <div
          v-for="key in keys"
          :key="key.id"
          class="pix-key-card flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <span
            class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-solid-2 text-n-slate-11"
            aria-hidden="true"
            ><span class="i-hugeicons-qr-code size-5"
          /></span>
          <div class="min-w-0 flex-1">
            <p class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ key.label }}
            </p>
            <p class="mb-1 break-all font-mono text-xs text-n-slate-12">
              {{ displayPixKey(key.key_type, key.key) }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t('PIX_KEYS.MERCHANT_SUMMARY', {
                  merchant: key.merchant_name,
                  city: key.merchant_city,
                })
              }}
            </p>
          </div>
          <div
            class="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto"
          >
            <span
              class="mr-auto rounded-md bg-n-solid-2 px-2 py-1 text-xs text-n-slate-11 sm:mr-2"
              >{{ typeLabels[key.key_type] }}</span
            >
            <NextButton
              :label="t('PIX_KEYS.EDIT')"
              variant="ghost"
              size="sm"
              icon="i-hugeicons-pencil-edit-02"
              :disabled="saving || !mfaEnabled || editingId !== null"
              @click="edit(key)"
            />
            <NextButton
              :label="t('PIX_KEYS.DELETE')"
              variant="ghost"
              color="ruby"
              icon="i-hugeicons-delete-02"
              size="sm"
              :disabled="saving || editingId !== null"
              @click="confirmDelete(key)"
            />
          </div>
        </div>
        <form
          v-if="editingId !== null"
          class="pix-key-form grid min-w-0 gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-4 sm:grid-cols-2 sm:p-5"
          @submit.prevent="save"
        >
          <div
            class="flex items-start gap-3 border-b border-n-weak pb-4 sm:col-span-2"
          >
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-solid-2 text-n-slate-11"
              aria-hidden="true"
              ><span class="i-hugeicons-qr-code size-5"
            /></span>
            <div class="min-w-0">
              <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{
                  t(editingId ? 'PIX_KEYS.FORM.EDIT' : 'PIX_KEYS.FORM.CREATE')
                }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('PIX_KEYS.FORM.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <NextInput
            v-model="form.label"
            :label="t('PIX_KEYS.LABEL')"
            :placeholder="t('PIX_KEYS.LABEL_PLACEHOLDER')"
            maxlength="50"
            :disabled="saving"
            required
          />
          <label class="grid gap-1.5 text-sm font-medium">
            {{ t('PIX_KEYS.TYPE') }}
            <NextSelect
              :model-value="form.key_type"
              :options="types"
              :disabled="saving"
              :aria-label="t('PIX_KEYS.TYPE')"
              @update:model-value="changeKeyType"
            />
          </label>
          <NextInput
            v-model="form.key"
            :type="keyInputType"
            :inputmode="form.key_type === 'CPF' ? 'numeric' : keyInputType"
            :label="t('PIX_KEYS.KEY')"
            :placeholder="form.key_type === 'PHONE' ? '(11) 99999-9999' : ''"
            :message="keyMessage"
            :message-type="keyValid ? 'success' : keyTouched ? 'error' : 'info'"
            :aria-invalid="keyTouched && !keyValid"
            autocapitalize="none"
            spellcheck="false"
            maxlength="100"
            :disabled="saving"
            required
            @blur="validateKey"
          />
          <NextInput
            v-model="form.merchant_name"
            :label="t('PIX_KEYS.MERCHANT')"
            maxlength="100"
            :disabled="saving"
            required
          />
          <NextInput
            v-model="form.merchant_city"
            :label="t('PIX_KEYS.CITY')"
            :message="t('PIX_KEYS.CITY_HELP')"
            maxlength="100"
            :disabled="saving"
            required
          />
          <p class="mb-0 text-xs text-n-slate-11 sm:col-span-2">
            {{ t('PIX_KEYS.BANK_NOTICE') }}
          </p>
          <div
            class="grid min-w-0 gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4 sm:col-span-2 sm:grid-cols-2"
          >
            <div class="min-w-0">
              <h4
                class="mb-1 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <span
                  class="i-hugeicons-shield-01 size-4"
                  aria-hidden="true"
                />{{ t('PIX_KEYS.SECURITY.CONFIRM') }}
              </h4>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  t(
                    needsDeliveryCode
                      ? 'PIX_KEYS.SECURITY.DELIVERY_HELP'
                      : 'PIX_KEYS.SECURITY.AUTHENTICATOR_HELP'
                  )
                }}
              </p>
            </div>
            <div class="grid min-w-0 gap-2">
              <NextInput
                v-model="otpCode"
                :label="t('PIX_KEYS.SECURITY.CODE')"
                placeholder="000000"
                inputmode="numeric"
                autocomplete="one-time-code"
                pattern="[0-9]{6}"
                maxlength="6"
                :disabled="saving || !mfaEnabled"
                required
              />
              <NextButton
                v-if="needsDeliveryCode"
                type="button"
                :label="
                  t(
                    codeSent
                      ? 'PIX_KEYS.SECURITY.RESEND'
                      : 'PIX_KEYS.SECURITY.SEND'
                  )
                "
                size="sm"
                variant="ghost"
                :is-loading="requestingCode"
                :disabled="requestingCode || saving || !mfaEnabled"
                @click="requestCode"
              />
              <p
                v-if="codeMessage"
                class="mb-0 text-xs text-n-slate-11"
                role="status"
              >
                {{ codeMessage }}
              </p>
            </div>
          </div>
          <div
            class="flex flex-wrap justify-end gap-2 border-t border-n-weak pt-4 sm:col-span-2"
          >
            <NextButton
              type="button"
              :label="t('PIX_KEYS.CANCEL')"
              variant="ghost"
              :disabled="saving"
              @click="cancel"
            />
            <NextButton
              type="submit"
              :label="t('PIX_KEYS.SAVE')"
              icon="i-hugeicons-lock-password"
              :disabled="!canSave || !codeValid || !mfaEnabled || saving"
              :is-loading="saving"
            />
          </div>
        </form>
      </template>
    </div>
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('PIX_KEYS.DELETE_TITLE')"
      :description="
        t('PIX_KEYS.DELETE_DESCRIPTION', { label: deletion?.label || '' })
      "
      :confirm-button-label="t('PIX_KEYS.DELETE')"
      :is-loading="saving"
      :disable-confirm-button="saving"
      @confirm="remove"
    />
  </SectionLayout>
</template>
